import {
  Button,
  CircularProgress,
  makeStyles,
  Modal,
  Paper,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import React, { useEffect, useState } from 'react'
import { Alert, Autocomplete } from '@material-ui/lab'
import {
  SubmitState,
  useNewUserGroup,
  UserGroupItem,
  UserProfile,
  useTrashUserProfile,
  useUpdateUserProfile,
  useUserGroupsList,
} from 'api'
import { strings } from 'strings'
import { useToken } from '../App'
import { PhoneVerifying } from '../verifying/PhoneVerifying'
import { ConfirmButton } from '../ConfirmButton'
import { useHistory } from 'react-router-dom'
import { ChangePassword } from './ChangePassword'
import { LoadingButton } from '../LoadingButton'

interface UserProfileFormProps {
  userProfile: UserProfile
}

export function UserProfileForm({ userProfile }: UserProfileFormProps) {
  const classes = useStyles()
  const { userId, userRole } = useToken()
  const history = useHistory()
  const updateUserProfile = useUpdateUserProfile()
  const trashUserProfile = useTrashUserProfile()
  const [isOpen, setOpen] = useState(false)
  const [isPassOpen, setPassOpen] = useState(false)
  const [submitState, setSubmitState] = useState(SubmitState.None)
  const {
    register,
    formState: { isDirty },
    handleSubmit,
    reset,
    control,
    errors,
  } = useForm<UserProfile>({
    mode: 'onChange',
    defaultValues: userProfile,
  })

  useEffect(() => {
    reset(userProfile)
  }, [userProfile, reset])

  const isProfileOfAuthenticatedUser = userId === userProfile.id

  const handleClose = () => {
    setOpen(false)
  }

  const onSubmit: SubmitHandler<UserProfile> = (formData) => {
    setSubmitState(SubmitState.Submitting)
    updateUserProfile({ ...userProfile, ...formData })
      .then(() => {
        setSubmitState(SubmitState.Success)
        reset(formData)
      })
      .catch((err) => {
        console.log(err.message)
        setSubmitState(SubmitState.Error)
      })
  }

  const handleDelete = () => {
    setSubmitState(SubmitState.Submitting)
    trashUserProfile(userProfile.id)
      .then(() => {
        history.replace('/users')
      })
      .catch((err) => {
        console.log(err)
        setSubmitState(SubmitState.Error)
      })
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {userRole !== 'regular' && !userProfile.phoneVerified && (
          <div className={classes.alert}>
            <Alert severity="warning">شماره موبایل تایید نشده است</Alert>
          </div>
        )}
        <div className={classes.fields}>
          <Typography className={classes.accountInfo}>{strings.accountInfo}</Typography>
          <div className={classes.section}>
            <CustomTextField
              name="username"
              label={strings.username}
              defaultValue={userProfile.username}
              disabled
            />
            <RoleSelect
              name="role"
              label={strings.role}
              inputRef={register}
              disabled={userRole === 'regular'}
            />
            {userRole !== 'regular' && <UserGroupSelector control={control} />}
          </div>
          <Typography className={classes.personalInfo}>{strings.personalInfo}</Typography>
          <div className={classes.section}>
            <CustomTextField name="fullname" label={strings.fullname} inputRef={register} />
            <CustomTextField
              label={strings.nationalCode}
              defaultValue={userProfile.nationalCode}
              disabled={true}
            />
            <CustomTextField name="state" label={strings.state} inputRef={register} />
            <CustomTextField name="city" label={strings.city} inputRef={register} />
            <CustomTextField name="legalEntity" label={strings.legalEntity} inputRef={register} />
            <CustomTextField
              label={strings.phoneNumber}
              defaultValue={userProfile.phoneNumber}
              disabled={true}
              onClick={isProfileOfAuthenticatedUser ? () => setOpen(true) : undefined}
            />
            <CustomTextField
              name="email"
              label={strings.email}
              inputRef={register({
                pattern:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              })}
              error={!!errors.email}
              fullWidth
              className={classes.email}
            />
          </div>
        </div>
        <div className={classes.submitSection}>
          <LoadingButton
            type="submit"
            variant="outlined"
            color="primary"
            text={strings.apply}
            disabled={!isDirty || submitState === SubmitState.Submitting}
            loading={submitState === SubmitState.Submitting}
          />
          {userRole === 'admin' && (
            <ConfirmButton
              type="button"
              variant="outlined"
              color="secondary"
              disabled={submitState === SubmitState.Submitting}
              onClick={handleDelete}
              className={classes.btnDelete}
              text={strings.delete}
            />
          )}
          {(userRole === 'admin' || isProfileOfAuthenticatedUser) && (
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={() => setPassOpen(true)}
            >
              {strings.changePassword}
            </Button>
          )}
        </div>
      </form>
      <Modal open={isPassOpen} onClose={() => setPassOpen(false)}>
        <ChangePassword
          username={userProfile.username}
          className={classes.modal}
          onCancel={() => setPassOpen(false)}
        />
      </Modal>
      {isProfileOfAuthenticatedUser && (
        <Modal disableBackdropClick disableEscapeKeyDown open={isOpen} onClose={handleClose}>
          <Paper className={classes.modal}>
            <PhoneVerifying
              userId={userProfile.id}
              phoneNumber={userProfile.phoneNumber}
              onCancel={handleClose}
              onSuccess={handleClose}
            />
          </Paper>
        </Modal>
      )}
    </>
  )
}

function CustomTextField(props: TextFieldProps) {
  return <TextField margin="dense" autoComplete="off" variant="outlined" {...props} />
}

function UserGroupSelector({ control, defaultValue }: any) {
  const newUserGroup = useNewUserGroup()
  const { userGroupsList, loading, error } = useUserGroupsList()

  if (loading) return <CircularProgress size={16} />
  if (error) return <Typography color="error">{error.message}</Typography>
  if (!userGroupsList) return <Typography color="error">{strings.notExists}</Typography>

  const handleFilterOptions = (options: Array<UserGroupItem>, params: any) => {
    if (!params.inputValue) return options

    let exact = false
    const value = params.inputValue.toUpperCase()
    const filtered: Array<any> = []

    for (const option of options) {
      const text = option.name.toUpperCase()
      if (value === text) exact = true
      if (text.indexOf(value) >= 0) filtered.push(option)
    }

    if (!exact) {
      filtered.push({
        inputValue: value,
        name: `${strings.creation} "${params.inputValue}"`,
      })
    }

    return filtered
  }

  const handleGroupChanged = (option: any, onChange: any) => {
    if (!option) {
      onChange(null)
    } else if (option.inputValue) {
      newUserGroup(option.inputValue)
        .then((groupId) => {
          userGroupsList.push({ id: groupId, name: option.inputValue, permissions: [] })
          onChange(groupId)
        })
        .catch((err) => {
          onChange(-1)
          console.log(err.message)
        })
    } else {
      onChange(option.id)
    }
  }

  return (
    <Controller
      name="groupId"
      control={control}
      defaultValue={defaultValue}
      render={({ value, onChange }) => {
        const selectedGroup: UserGroupItem | undefined =
          value && userGroupsList.find((x) => x.id === value)
        let helperText = strings.noUserGroupMeansInactive
        if (selectedGroup) {
          helperText = selectedGroup.permissions.map((x) => x.name).join(',')
        }
        return (
          <Autocomplete
            freeSolo
            clearOnBlur
            options={userGroupsList.sort((a, b) => a.id - b.id)}
            renderOption={(x) => x.name}
            getOptionLabel={(x: any) => (x.inputValue ? x.inputValue : x.name)}
            value={selectedGroup}
            onChange={(e, o) => handleGroupChanged(o, onChange)}
            filterOptions={handleFilterOptions}
            style={{ gridColumn: '1/3' }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                autoComplete="off"
                variant="outlined"
                helperText={helperText}
                label={strings.groupName}
              />
            )}
          />
        )
      }}
    />
  )
}

function RoleSelect(props: TextFieldProps) {
  return (
    <CustomTextField select SelectProps={{ native: true }} {...props}>
      <option value="regular">{strings.regular}</option>
      <option value="operator">{strings.operator}</option>
      <option value="admin">{strings.admin}</option>
    </CustomTextField>
  )
}

const useStyles = makeStyles((theme) => ({
  section: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gridGap: theme.spacing(0.5, 1.5),
  },
  fields: {
    padding: theme.spacing(2),
  },
  accountInfo: {
    margin: theme.spacing(0.5, 0, 1.5, 0),
  },
  personalInfo: {
    margin: theme.spacing(4, 0, 1.5, 0),
  },
  email: {
    flip: false,
    direction: 'ltr',
    gridColumn: '1/3',
  },
  submitSection: {
    flip: false,
    zIndex: 5,
    position: 'sticky',
    bottom: 0,
    padding: theme.spacing(1, 2),
    boxShadow: '0px -1px 1px 0px rgba(0, 0, 0, 0.1)',
    background: '#fff',
    alignItems: 'center',
    '& button': {
      flip: false,
      marginLeft: theme.spacing(2),
    },
  },
  btnDelete: {
    flip: false,
    marginLeft: '0 !important',
    float: 'left',
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      width: `calc(100% - ${theme.spacing(2)}px)`,
      transform: 'none',
      top: 0,
      left: 0,
      margin: theme.spacing(1),
    },
    '&:focus': {
      outline: 'none',
    },
  },
  alert: {
    width: `calc(100% - ${theme.spacing(4)}px)`,
    margin: theme.spacing(1, 2, 0, 2),
    '& .MuiAlert-root': {
      width: '100%',
    },
  },
}))
